/**
 * Stage 36 · Unified Work Inbox — a single "For me" queue across every module.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import FlowExceptionsStrip from "@/components/oneflow/FlowExceptionsStrip";
import { CheckSquare, AlertOctagon, Radar, ShieldAlert, Inbox, FileCheck2, ListChecks, Stamp, PackageCheck } from "lucide-react";

const KIND_CFG = {
  intake_review:        { icon: Inbox,        color: "bg-indigo-100 text-indigo-800 border-indigo-200", to: "/intake" },
  automation_approval:  { icon: CheckSquare,  color: "bg-amber-100 text-amber-800 border-amber-200",   to: "/automation" },
  security_exception:   { icon: ShieldAlert,  color: "bg-rose-100 text-rose-800 border-rose-200",      to: "/security" },
  control_exception:    { icon: AlertOctagon, color: "bg-orange-100 text-orange-800 border-orange-200",to: "/trust" },
  one_approval:         { icon: Stamp,        color: "bg-emerald-100 text-emerald-800 border-emerald-200", to: "/approvals/one" },
  form_approval:        { icon: FileCheck2,   color: "bg-violet-100 text-violet-800 border-violet-200", to: "/forms/one-forms" },
  my_task:              { icon: ListChecks,   color: "bg-sky-100 text-sky-800 border-sky-200",         to: "/work/projects" },
  rma:                  { icon: PackageCheck, color: "bg-orange-100 text-orange-800 border-orange-200", to: "/service/rma" },
  request:              { icon: Radar,        color: "bg-sky-100 text-sky-800 border-sky-200",         to: "/requests" },
};

export default function WorkOne() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("for_me");
  useEffect(() => { api.get("/work/for-me").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  const t = d?.tiles || {};
  return (
    <div className="max-w-6xl space-y-5" data-testid="work-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Work</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">For me</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">One queue across every module — approvals, exceptions, intake reviews, requests. Nothing gets lost.</p>
      </div>
      <FlowExceptionsStrip />
      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {["for_me", "waiting", "completed"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`work-tab-${k}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            {k.replace("_", " ")}
          </button>
        ))}
      </nav>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Action required"    value={t.action_required} tone="warn" testid="work-tile-action" />
        <Tile label="Approvals for me"   value={t.one_approvals} testid="work-tile-one-approvals" />
        <Tile label="Form approvals"     value={t.form_approvals} testid="work-tile-form-approvals" />
        <Tile label="My tasks"           value={t.my_tasks} tone={t.my_tasks_overdue ? "warn" : ""} testid="work-tile-my-tasks" />
        <Tile label="RMA action"         value={t.rma_action} tone={t.rma_action ? "warn" : ""} testid="work-tile-rma" />
        <Tile label="Billing exceptions" value={t.billing_exceptions} tone={t.billing_exceptions ? "critical" : ""} testid="work-tile-billing" />
        <Tile label="Exceptions open"    value={t.exceptions_open} tone="critical" />
        <Tile label="Automation approvals" value={t.automation_approvals} />
        <Tile label="Intake review"      value={t.intake_review} />
        <Tile label="Overdue tasks"      value={t.my_tasks_overdue} tone={t.my_tasks_overdue ? "critical" : ""} testid="work-tile-overdue" />
      </div>
      {d?.message ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-4 text-sm" data-testid="work-healthy">
          {d.message}
        </div>
      ) : (
        <ul className="space-y-2">
          {(d?.items || []).map((it, i) => {
            const cfg = KIND_CFG[it.kind] || KIND_CFG.request;
            const Icon = cfg.icon;
            return (
              <li key={i} className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3" data-testid={`work-item-${it.id || i}`}>
                <span className={"inline-flex items-center justify-center h-8 w-8 rounded border " + cfg.color}><Icon className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{it.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{it.kind} · {(it.at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                {it.chip && <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + cfg.color}>{it.chip}</span>}
                <a href={cfg.to} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">Open</a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Tile({ label, value, tone, testid }) {
  const cls = tone === "warn" ? "text-amber-700" : tone === "critical" ? "text-rose-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-2xl font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
    </div>
  );
}
